import { lazy, type ComponentType } from 'react';
import { Route, Routes } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { VisitLogger } from '@/components/VisitLogger';

import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/** Wrap a named page export in `React.lazy` (keeps the router file terse). */
function lazyPage(name: string, loader: () => Promise<Record<string, unknown>>) {
  return lazy(() => loader().then((mod) => ({ default: mod[name] as ComponentType })));
}

// Public pages (code-split so the initial bundle stays small).
const HomePage = lazyPage('HomePage', () => import('@/pages/public/HomePage'));
const AnnouncementsPage = lazyPage(
  'AnnouncementsPage',
  () => import('@/pages/public/AnnouncementsPage'),
);
const AnnouncementDetailPage = lazyPage(
  'AnnouncementDetailPage',
  () => import('@/pages/public/AnnouncementDetailPage'),
);
const EventsPage = lazyPage('EventsPage', () => import('@/pages/public/EventsPage'));
const UmkmPage = lazyPage('UmkmPage', () => import('@/pages/public/UmkmPage'));
const UmkmDetailPage = lazyPage('UmkmDetailPage', () => import('@/pages/public/UmkmDetailPage'));
const PublicPadiPage = lazyPage('PadiPage', () => import('@/pages/public/PadiPage'));
const PemilihPublicPage = lazyPage('PemilihPublicPage', () => import('@/pages/public/PemilihPage'));
const InfrastructurePage = lazyPage(
  'InfrastructurePage',
  () => import('@/pages/public/InfrastructurePage'),
);
const HealthPage = lazyPage('HealthPage', () => import('@/pages/public/HealthPage'));
const ContactPage = lazyPage('ContactPage', () => import('@/pages/public/ContactPage'));
const PublicLayananPage = lazyPage('LayananPage', () => import('@/pages/public/LayananPage'));
const ProfilPage = lazyPage('ProfilPage', () => import('@/pages/public/ProfilPage'));
const DaftarPenjualPage = lazyPage(
  'DaftarPenjualPage',
  () => import('@/pages/public/DaftarPenjualPage'),
);

// Dashboard pages.
const DashboardHome = lazyPage('DashboardHome', () => import('@/pages/app/DashboardHome'));
const LayananPage = lazyPage('LayananPage', () => import('@/pages/app/LayananPage'));
const WargaPage = lazyPage('WargaPage', () => import('@/pages/app/WargaPage'));
const DataPage = lazyPage('DataPage', () => import('@/pages/app/DataPage'));
const NiagaPage = lazyPage('NiagaPage', () => import('@/pages/app/NiagaPage'));
const TokoPage = lazyPage('TokoPage', () => import('@/pages/app/TokoPage'));
const PadiPage = lazyPage('PadiPage', () => import('@/pages/app/PadiPage'));
const PupukPage = lazyPage('PupukPage', () => import('@/pages/app/PupukPage'));
const PenjualPage = lazyPage('PenjualPage', () => import('@/pages/app/PenjualPage'));
const InfrastrukturPage = lazyPage(
  'InfrastrukturPage',
  () => import('@/pages/app/InfrastrukturPage'),
);
const AmanPage = lazyPage('AmanPage', () => import('@/pages/app/AmanPage'));
const SehatPage = lazyPage('SehatPage', () => import('@/pages/app/SehatPage'));
const PemilihPage = lazyPage('PemilihPage', () => import('@/pages/app/PemilihPage'));
const DataJiwaPage = lazyPage('DataJiwaPage', () => import('@/pages/app/DataJiwaPage'));
const KematianPage = lazyPage('KematianPage', () => import('@/pages/app/KematianPage'));
const PerpindahanPage = lazyPage('PerpindahanPage', () => import('@/pages/app/PerpindahanPage'));
const KelahiranPage = lazyPage('KelahiranPage', () => import('@/pages/app/KelahiranPage'));
const NotificationsPage = lazyPage(
  'NotificationsPage',
  () => import('@/pages/app/NotificationsPage'),
);
const UsersPage = lazyPage('UsersPage', () => import('@/pages/app/UsersPage'));
const AuditPage = lazyPage('AuditPage', () => import('@/pages/app/AuditPage'));
const AccessLogPage = lazyPage('AccessLogPage', () => import('@/pages/app/AccessLogPage'));
const SettingsPage = lazyPage('SettingsPage', () => import('@/pages/app/SettingsPage'));

export function App() {
  return (
    <>
      <VisitLogger />
      <Routes>
        <Route path="/masuk" element={<LoginPage />} />

        <Route element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="pengumuman" element={<AnnouncementsPage />} />
          <Route path="pengumuman/:id" element={<AnnouncementDetailPage />} />
          <Route path="agenda" element={<EventsPage />} />
          <Route path="umkm" element={<UmkmPage />} />
          <Route path="umkm/:id" element={<UmkmDetailPage />} />
          <Route path="padi" element={<PublicPadiPage />} />
          <Route path="pemilih" element={<PemilihPublicPage />} />
          <Route path="daftar-penjual" element={<DaftarPenjualPage />} />
          <Route path="layanan" element={<PublicLayananPage />} />
          <Route path="profil" element={<ProfilPage />} />
          <Route path="infrastruktur" element={<InfrastructurePage />} />
          <Route path="kesehatan" element={<HealthPage />} />
          <Route path="kontak" element={<ContactPage />} />
        </Route>

        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="layanan" element={<LayananPage />} />
          <Route path="warga" element={<WargaPage />} />
          <Route path="data" element={<DataPage />} />
          <Route path="niaga" element={<NiagaPage />} />
          <Route path="toko" element={<TokoPage />} />
          <Route path="padi" element={<PadiPage />} />
          <Route path="pupuk" element={<PupukPage />} />
          <Route path="penjual" element={<PenjualPage />} />
          <Route path="infrastruktur" element={<InfrastrukturPage />} />
          <Route path="aman" element={<AmanPage />} />
          <Route path="sehat" element={<SehatPage />} />
          <Route path="pemilih" element={<PemilihPage />} />
          <Route path="jiwa" element={<DataJiwaPage />} />
          <Route path="kematian" element={<KematianPage />} />
          <Route path="perpindahan" element={<PerpindahanPage />} />
          <Route path="kelahiran" element={<KelahiranPage />} />
          <Route path="notifikasi" element={<NotificationsPage />} />
          <Route path="pengguna" element={<UsersPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="akses" element={<AccessLogPage />} />
          <Route path="pengaturan" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
